import { type CSSProperties, useState } from 'react';
import { IconClose, IconLeftCaret, IconRightCaret } from '@belong/icons';
import { Text } from '@belong/ui';
import { clsx } from 'clsx';
import { BREAKPOINTS_WIDTHS } from 'consts/breakpoints';
import { useWindowSize } from 'hooks/useWindowSize';
import {
  type Availability,
  type RangeKey,
  TIME_RANGES,
  formatDateOnly,
} from 'components/AvailabilityPicker/availability-utils';
import {
  countSlots,
  formatSlotLabel,
  formatSlotParts,
  getDisplaySlots,
  getPlaceholderText,
  hasAllDay,
  hasSlot,
  removeSlot,
  selectDate,
  toggleSlot,
} from './smart-lock-install-availability.utils';

const DATES_PER_PAGE = 4;
const TILE_HEIGHT = 110;
const MOBILE_TIME_TILE_HEIGHT = 80;
const MOBILE_DAY_TILE_WIDTH = 140;

type SmartLockAvailabilityPickerProps = {
  /** Selectable `yyyy-MM-dd` dates, in order. */
  dates: string[];
  timeFrames: RangeKey[];
  /** Exact number of slots (one time frame on one date) to collect. */
  requiredSlots: number;
  value: Availability[];
  onChange: (next: Availability[]) => void;
  title?: string;
};

/**
 * Collects an exact number of date + time frame slots. Days are paged on
 * desktop and scroll horizontally on mobile.
 */
export function SmartLockAvailabilityPicker({
  dates,
  timeFrames,
  requiredSlots,
  value,
  onChange,
  title = `Select ${requiredSlots} Times`,
}: SmartLockAvailabilityPickerProps) {
  const { width } = useWindowSize();
  const isMobile = (width ?? BREAKPOINTS_WIDTHS.MD) < BREAKPOINTS_WIDTHS.MD;

  const [activeDate, setActiveDate] = useState<string | undefined>(
    () => value.find((s) => dates.includes(s.date))?.date ?? dates[0],
  );
  const [page, setPage] = useState(() =>
    Math.max(0, Math.floor(dates.indexOf(activeDate ?? '') / DATES_PER_PAGE)),
  );

  const lastPage = Math.max(0, Math.ceil(dates.length / DATES_PER_PAGE) - 1);
  const slotCount = countSlots(value);
  const isFull = slotCount >= requiredSlots;
  const remaining = Math.max(0, requiredSlots - slotCount);
  const slots = getDisplaySlots(value);
  // A time frame swaps out a picked "All Day", so it stays enabled.
  const isAllDayPicked = hasAllDay(value);

  const handleDateClick = (date: string) => {
    setActiveDate(date);
    const next = selectDate(value, date);
    if (next !== value) onChange(next);
  };

  const visibleDates = isMobile
    ? dates
    : dates.slice(page * DATES_PER_PAGE, (page + 1) * DATES_PER_PAGE);
  const spacers = isMobile ? 0 : DATES_PER_PAGE - visibleDates.length;

  return (
    <div className="w-full">
      <div className="flex flex-col items-center">
        <Text fontWeight="semibold">{title}</Text>
        <div className="mt-2xs flex" style={{ gap: 5 }}>
          {Array.from({ length: requiredSlots }, (_, i) => (
            <div
              key={i}
              style={{ width: 18, height: 4 }}
              className={clsx(
                'rounded-full',
                i < slotCount ? 'bg-green' : 'bg-gray',
              )}
            />
          ))}
        </div>
      </div>

      {isMobile ? (
        <div
          className="-mx-sm mt-2sm flex overflow-x-auto px-sm pb-xs pt-2xs"
          style={{ gap: 21, scrollbarWidth: 'none' }}
        >
          {visibleDates.map((date) => (
            <DayTile
              key={date}
              date={date}
              isActive={date === activeDate}
              onClick={() => handleDateClick(date)}
              style={{ flex: `0 0 ${MOBILE_DAY_TILE_WIDTH}px` }}
            />
          ))}
        </div>
      ) : (
        <div className="mt-sm flex items-center" style={{ gap: 20 }}>
          <button
            type="button"
            aria-label="Previous dates"
            className={clsx(
              'flex flex-none',
              page === 0 ? 'cursor-default text-dark-gray' : 'text-navy',
            )}
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <IconLeftCaret width={12} height={22} />
          </button>
          <div className="flex min-w-0 flex-1 py-2xs" style={{ gap: 21 }}>
            {visibleDates.map((date) => (
              <DayTile
                key={date}
                date={date}
                isActive={date === activeDate}
                onClick={() => handleDateClick(date)}
                style={{ flex: '1 1 0' }}
              />
            ))}
            {Array.from({ length: spacers }, (_, i) => (
              <div key={`spacer-${i}`} style={{ flex: '1 1 0' }} />
            ))}
          </div>
          <button
            type="button"
            aria-label="Next dates"
            className={clsx(
              'flex flex-none',
              page >= lastPage ? 'cursor-default text-dark-gray' : 'text-navy',
            )}
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            <IconRightCaret width={12} height={22} />
          </button>
        </div>
      )}

      {activeDate && (
        <div
          className={clsx('flex', isMobile ? 'mt-2sm flex-col' : 'mt-sm')}
          style={{ gap: 21, padding: isMobile ? 0 : '0 32px' }}
        >
          {timeFrames.map((time) => {
            const slot = { date: activeDate, time };
            const isAdded = hasSlot(value, slot);
            return (
              <TimeTile
                key={time}
                time={time}
                isAdded={isAdded}
                isDisabled={
                  !isAdded && isFull && time !== 'AllDay' && !isAllDayPicked
                }
                fill={!isMobile}
                onClick={() => onChange(toggleSlot(value, slot, requiredSlots))}
              />
            );
          })}
        </div>
      )}

      <div
        className="flex flex-col"
        style={{ gap: isMobile ? 20 : 8, marginTop: isMobile ? 40 : 48 }}
      >
        {slots.map((slot) => {
          const parts = formatSlotParts(slot);
          return (
            <div
              key={`${slot.date}-${slot.time}`}
              className="flex items-center justify-between"
              style={{ minHeight: 32, gap: 16 }}
            >
              {isMobile ? (
                <div className="flex flex-col">
                  <Text fontWeight="semibold">{parts.date}</Text>
                  <Text fontWeight="semibold">{parts.time}</Text>
                </div>
              ) : (
                <Text fontWeight="semibold">{formatSlotLabel(slot)}</Text>
              )}
              <button
                type="button"
                aria-label={`Remove ${formatSlotLabel(slot)}`}
                className="flex flex-none p-2xs text-navy hover:text-dark-navy"
                onClick={() => onChange(removeSlot(value, slot))}
              >
                <IconClose width={13} height={13} />
              </button>
            </div>
          );
        })}
        {remaining > 0 && (
          <div
            className="flex items-center rounded border border-dashed border-gray text-dark-gray"
            style={{ height: 32, padding: '0 12px' }}
          >
            {getPlaceholderText(remaining)}
          </div>
        )}
      </div>
    </div>
  );
}

type DayTileProps = {
  date: string;
  isActive: boolean;
  onClick: () => void;
  style: CSSProperties;
};

function DayTile({ date, isActive, onClick, style }: DayTileProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={clsx(
        'flex min-w-0 flex-col items-center justify-center rounded shadow transition-colors duration-hover',
        isActive
          ? 'bg-navy text-white'
          : 'bg-white text-navy hover:bg-light-gray',
      )}
      style={{ ...style, height: TILE_HEIGHT, gap: 8 }}
    >
      <span className="text-body font-semibold">
        {formatDateOnly(date, 'EEEE')}
      </span>
      <span className="text-p1">{formatDateOnly(date, 'MMM d, yyyy')}</span>
    </button>
  );
}

type TimeTileProps = {
  time: RangeKey;
  isAdded: boolean;
  isDisabled: boolean;
  /** Share the row width (desktop) instead of stacking at full width. */
  fill: boolean;
  onClick: () => void;
};

function TimeTile({ time, isAdded, isDisabled, fill, onClick }: TimeTileProps) {
  const range = TIME_RANGES[time];
  return (
    <button
      type="button"
      aria-pressed={isAdded}
      disabled={isDisabled}
      onClick={onClick}
      className={clsx(
        'flex min-w-0 flex-col items-center justify-center rounded transition-colors duration-hover',
        isAdded && 'bg-navy text-white shadow',
        isDisabled &&
          'cursor-not-allowed border border-solid border-gray bg-light-gray text-dark-gray',
        !isAdded &&
          !isDisabled &&
          'bg-white text-navy shadow hover:bg-light-gray',
      )}
      style={{
        flex: fill ? '1 1 0' : 'none',
        height: fill ? TILE_HEIGHT : MOBILE_TIME_TILE_HEIGHT,
        gap: 4,
      }}
    >
      <span className="text-body">{range.label}</span>
      <span className="text-p1">{range.timeRange}</span>
    </button>
  );
}
